import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  platformBrandingService,
  type PublicPlatformBranding,
} from '@/services/platform-branding.service';

const QUERY_KEY = ['platform-branding'];

/**
 * Logo & favicon SIPAUD yang diatur Super Admin.
 *
 * Memakai endpoint publik, bukan yang di konsol platform, karena pemakainya
 * termasuk halaman login — di sana belum ada sesi sama sekali. Gagal
 * diam-diam (`null`): merek yang tidak termuat tidak boleh menghalangi orang
 * masuk, dan tiap pemanggil sudah punya lambang bawaan.
 */
export function usePlatformBranding(): PublicPlatformBranding {
  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => platformBrandingService.getPublic(),
    // Merek platform praktis tidak pernah berubah; sekali per sesi cukup.
    staleTime: Infinity,
    retry: false,
  });

  return data ?? { logoUrl: null, faviconUrl: null };
}

/**
 * Menukar <link rel="icon"> di index.html dengan favicon unggahan.
 *
 * Ditukar di runtime, bukan di HTML, sebab apps/app adalah SPA yang
 * di-build sekali lalu dipakai semua orang — favicon-nya baru diketahui
 * setelah API menjawab. Selama itu tab menampilkan favicon.svg bawaan,
 * yang juga menjadi keadaan akhir bila Super Admin belum menggantinya.
 */
export function usePlatformFavicon(): void {
  const { faviconUrl } = usePlatformBranding();

  useEffect(() => {
    if (!faviconUrl) return;

    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;

    const previousHref = link.href;
    const previousType = link.type;

    link.href = faviconUrl;
    // Dikosongkan karena tipe bawaannya image/svg+xml — dibiarkan terpasang,
    // favicon PNG akan diabaikan peramban sebagai berkas yang salah tipe.
    link.type = '';

    return () => {
      link.href = previousHref;
      link.type = previousType;
    };
  }, [faviconUrl]);
}
